<template>
  <div class="quick-operation-container">
    <!-- 页面标题与面包屑（专业感） -->
    <div class="page-header">
      <div class="header-left">
        <el-breadcrumb separator="/">
          <el-breadcrumb-item>首页</el-breadcrumb-item>
          <el-breadcrumb-item>收银台</el-breadcrumb-item>
          <el-breadcrumb-item>快捷操作</el-breadcrumb-item>
        </el-breadcrumb>
      </div>
      <div class="header-right">
        <div class="confirm-toggle-header">
          <el-switch v-model="confirmToggle" active-text="开启确认" inactive-text="关闭确认" size="small" />
          <span class="toggle-hint-header">操作确认</span>
        </div>
        <el-button type="primary" plain icon="el-icon-refresh" @click="getdata" size="small">刷新列表</el-button>
      </div>
    </div>

    <!-- 主卡片：搜索区 + 用户信息迷你卡片（当有用户时） -->
    <el-card class="main-card" shadow="hover">
      <!-- 搜索区域 - 强化输入即匹配的交互 -->
      <div class="search-wrapper" :class="{ 'has-result': currentUser }">
        <el-autocomplete v-model="searchForm.keyword" :fetch-suggestions="querySearch" placeholder="请输入手机号 / 车牌号 / 姓名"
          :trigger-on-focus="false" @select="handleSelectSuggestion" @keyup.enter="searchUser" clearable size="large"
          class="search-input" popper-class="user-suggestion-popper">
          <template #prefix>
            <i class="el-icon-search"></i>
          </template>
          <template #suffix>
            <el-button type="primary" @click="searchUser" :loading="searchResult === 'searching'"
              class="search-btn">搜索</el-button>
          </template>
          <!-- 自定义建议项模板，显示更丰富信息 -->
          <template #default="{ item }">
            <div class="suggestion-item">
              <el-avatar size="small" :style="{ backgroundColor: item.color || '#409EFF' }">{{
                item.name.charAt(0).toUpperCase() }}</el-avatar>
              <div class="suggestion-info">
                <span class="suggestion-name">{{ item.name }}</span>
                <span class="suggestion-phone">{{ item.phone }}</span>
                <span v-if="item.carnum" class="suggestion-carnum">{{ item.carnum }}</span>
              </div>
            </div>
          </template>
        </el-autocomplete>
      </div>

      <!-- 搜索状态提示（无结果/错误） 美化后更精致 -->
      <transition name="fade">
        <div v-if="searchResult === 'not found'" class="result-message error">
          <i class="el-icon-info"></i> 未找到该用户，请尝试其他关键词
        </div>
        <div v-else-if="searchResult === 'searching'" class="result-message info">
          <i class="el-icon-loading"></i> 正在搜索...
        </div>
      </transition>

      <!-- 用户信息迷你卡片 - 仅当有currentUser时显示，紧凑展示 -->
      <div v-if="currentUser" class="user-mini-card">
        <div class="user-mini-avatar">
          <el-avatar :size="48" :style="{ backgroundColor: currentUser.color || '#409EFF' }">{{
            currentUser.name.charAt(0)
            }}</el-avatar>
        </div>
        <div class="user-mini-info">
          <div class="user-mini-row">
            <span class="label">姓名</span>
            <span class="value">{{ currentUser.name }}</span>
            <span class="label">ID</span>
            <span class="value">{{ currentUser.id }}</span>
          </div>
          <div class="user-mini-row">
            <span class="label">手机</span>
            <span class="value">{{ currentUser.phone }}</span>
            <span class="label">车牌</span>
            <span class="value">{{ currentUser.carnum || '-' }}</span>
          </div>
          <div class="user-mini-row highlight">
            <span class="label">余额</span>
            <span class="value balance">{{ currentUser.money / 100 || 0 }} 元</span>
            <span class="label">积分</span>
            <span class="value balance">{{ currentUser.points || 0 }}</span>
            <span class="label">折扣</span>
            <span class="value">{{ currentUser.discount || 100 }}%</span>
            <span class="label">单价</span>
            <span class="value">{{ currentUser.price / 100 || 0 }} 元/升</span>
          </div>
        </div>
        <!-- 快捷操作按钮直接附在卡片右侧，便于直接触发 -->
        <div class="user-mini-actions">
          <el-button type="primary" plain @click="activeTab = 'recharge'; showUserDialog = true">充值</el-button>
          <el-button type="success" plain @click="handleOpenRefuelDialog">加油</el-button>
        </div>
      </div>

      <!-- 如果没有用户，显示一个简洁的占位提示 -->
      <div v-else class="no-user-placeholder">
        <i class="el-icon-user"></i>
        <p>搜索并选择用户后，即可快速充值或加油</p>
      </div>
    </el-card>

    <!-- 操作弹窗 - 更专注于操作，用户信息精简后，弹窗主要承载表单 -->
    <el-dialog v-model="showUserDialog" :title="activeTab === 'recharge' ? '充值操作' : '加油操作'" width="500px"
      destroy-on-close center custom-class="operation-dialog">
      <!-- 弹窗内再展示一次极简用户标识，确保上下文清晰 -->
      <div class="dialog-user-context" v-if="currentUser">
        <el-tag type="info" size="medium" effect="plain">{{ currentUser.name }} ({{ currentUser.phone }})</el-tag>
        <el-tag type="warning" size="medium" effect="plain" v-if="currentUser.money">余额: {{ currentUser.money / 100
          }}元</el-tag>
        <el-tag type="success" size="medium" effect="plain" v-if="currentUser.discount">折扣: {{ currentUser.discount
          }}%</el-tag>
      </div>

      <el-tabs v-model="activeTab" class="dialog-tabs" :stretch="true">
        <!-- 充值Tab -->
        <el-tab-pane label="充值" name="recharge">
          <el-form :model="rechargeForm" label-position="top" class="operation-form">
            <el-form-item label="充值金额（元）" required>
              <el-input v-model.number="rechargeForm.amount" placeholder="请输入金额" size="large">
                <template #prefix><i class="el-icon-money"></i></template>
              </el-input>
            </el-form-item>
            <el-form-item label="优惠活动" class="flex-item" v-if="canshowres">
              <el-select v-model="rechargeForm.chargem" placeholder="请选择优惠" size="large">
                <el-option label="不使用优惠" value="0" />
                <el-option v-for="item in chargesys" :label="item.name" :value="item.givem" />
              </el-select>
            </el-form-item>
            <el-form-item label="备注（选填）">
              <el-input v-model="rechargeForm.remark" type="textarea" :rows="3" placeholder="可输入备注信息" />
            </el-form-item>
          </el-form>
          <div class="dialog-footer-actions">
            <el-button type="primary" size="large" @click="handleRecharge" :loading="rechargeLoading">确认充值</el-button>
          </div>
        </el-tab-pane>

        <!-- 加油Tab -->
        <el-tab-pane label="加油" name="refuel">
          <el-form :model="refuelForm" label-position="top" class="operation-form">
            <!-- 油类型 & 使用用户油价开关 放在一行更紧凑 -->
            <div class="form-row">
              <el-form-item :label="`油类型(` + (useUserPrice ? '用户油价' : '系统油价') + `)`" class="flex-item"
                v-if="!useUserPrice">
                <el-select v-model="refuelForm.oilType" placeholder="请选择" size="large">
                  <el-option label="92#汽油" value="92" />
                  <el-option label="95#汽油" value="95" />
                  <el-option label="98#汽油" value="98" />
                  <el-option label="0#柴油" value="0" />
                </el-select>
              </el-form-item>
              <el-form-item label=" " class="flex-item switch-item">
                <el-switch v-model="useUserPrice" active-text="用户油" inactive-text="系统油" size="large" />
              </el-form-item>
            </div>
            <el-form-item label="结算方式">
              <el-radio-group v-model="refuelForm.paymentMethod" size="large">
                <el-radio label="balance">余额支付</el-radio>
                <el-radio label="offline">线下支付</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="加油金额（元）">
              <el-input v-model="refuelForm.amount" placeholder="输入金额自动计算升数" size="large" @input="calculateLiter">
                <template #prefix><i class="el-icon-coin"></i></template>
              </el-input>
            </el-form-item>
            <el-form-item label="加油升数（L）">
              <el-input v-model.number="refuelForm.liter" placeholder="输入升数自动计算金额" size="large"
                @input="calculateAmount">
                <template #prefix><i class="el-icon-data-line"></i></template>
              </el-input>
            </el-form-item>
            <el-form-item label="优惠券" v-if="availableCoupons.length > 0">
              <el-select v-model="refuelForm.selectedCoupon" placeholder="选择优惠券" size="large" @change="calculateWithCoupon">
                <el-option label="不使用优惠券" value="" />
                <el-option 
                  v-for="coupon in availableCoupons" 
                  :key="coupon.id" 
                  :label="`${coupon.name} - ¥${coupon.money / 100} (满¥${coupon.minmoney / 100}可用)`" 
                  :value="coupon" 
                />
              </el-select>
            </el-form-item>
            <div class="price-preview">
              <span>单价：<strong>{{ currentOilPrice }} 元/升</strong></span>
              <span>折扣后应付：<strong class="actual-amount">{{ refuelForm.actualAmount || 0 }} 元</strong></span>
              <span v-if="refuelForm.selectedCoupon">优惠后：<strong class="final-amount">{{ calculateFinalAmount() }} 元</strong></span>
            </div>
          </el-form>
          <div class="dialog-footer-actions">
            <el-button type="success" size="large" @click="handleRefuel" :loading="refuelLoading">确认加油</el-button>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-dialog>

    <!-- 确认弹窗 -->
    <el-dialog v-model="showConfirmDialog" :title="confirmType === 'recharge' ? '充值确认' : '加油确认'" width="400px" center>
      <div class="confirm-dialog-content">
        <div class="confirm-item">
          <span class="confirm-label">用户：</span>
          <span class="confirm-value">{{ currentUser?.name }} ({{ currentUser?.phone }})</span>
        </div>
        <div class="confirm-item" v-if="confirmType === 'recharge'">
          <span class="confirm-label">充值金额：</span>
          <span class="confirm-value amount">{{ rechargeForm.amount }} 元</span>
        </div>
        <div class="confirm-item" v-else>
          <span class="confirm-label">加油金额：</span>
          <span class="confirm-value amount">{{ refuelForm.actualAmount || refuelForm.amount }} 元</span>
        </div>
        <div class="confirm-item" v-if="confirmType === 'refuel'">
          <span class="confirm-label">加油升数：</span>
          <span class="confirm-value">{{ refuelForm.liter }} L</span>
        </div>
        <div class="confirm-item" v-if="confirmType === 'refuel'">
          <span class="confirm-label">油类型：</span>
          <span class="confirm-value">{{ refuelForm.oilType }}#</span>
        </div>
        <div class="confirm-item" v-if="confirmType === 'refuel'">
          <span class="confirm-label">单价：</span>
          <span class="confirm-value">{{ currentOilPrice }} 元/升</span>
        </div>
        <div class="confirm-item" v-if="confirmType === 'refuel' && refuelForm.selectedCoupon">
          <span class="confirm-label">优惠券：</span>
          <span class="confirm-value amount">-¥{{ refuelForm.selectedCoupon.money / 100 }}</span>
        </div>
        <div class="confirm-item" v-if="confirmType === 'refuel'">
          <span class="confirm-label">最终应付：</span>
          <span class="confirm-value amount">{{ calculateFinalAmount() }} 元</span>
        </div>
      </div>
      <div class="dialog-footer">
        <el-button @click="showConfirmDialog = false">取消</el-button>
        <el-button type="primary" @click="confirmOperation">确认操作</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'QuickOperation',
  data() {
    return {
      searchForm: { keyword: '' },
      searchResult: '',
      userList: [],
      filteredUsers: [],
      showSuggestions: false,
      currentUser: null,
      showUserDialog: false,
      activeTab: 'recharge',
      rechargeForm: { amount: '', remark: '' },
      refuelForm: { amount: '', liter: '', oilType: '92', actualAmount: '', paymentMethod: 'balance', selectedCoupon: null },
      useUserPrice: false,
      systemOilPrices: {},
      // 加载状态
      rechargeLoading: false,
      refuelLoading: false,
      config: {},
      chargesys: [],
      // 确认开关
      confirmToggle: true,
      // 确认弹窗
      showConfirmDialog: false,
      confirmType: '',
      confirmData: {},
      // 优惠券相关
      availableCoupons: [],
      couponLoading: false,
    };
  },
  computed: {
    currentOilPrice() {
      if (this.useUserPrice && this.currentUser?.price) {
        return this.currentUser.price /100;
      }
      return this.systemOilPrices[this.refuelForm.oilType] / 100 || 0;
    },
    canshowres() {
      if (this.chargesys && this.chargesys.length && this.rechargeForm.amount > 0) {
        for (let o of this.chargesys) {
          if (this.rechargeForm.amount >= o.maxm) {
            return true
          }
        }
        return false
      } else {
        return false
      }
    },
  },
  mounted() {
    this.getdata();
    // 为每个用户随机生成一个头像背景色（仅用于展示）
  },
  methods: {
    async reloaduser() {
      try {
        const ret = await this.$api.bgyx.alluser();
        this.userList = (ret.data || []).map(u => ({
          ...u,
          color: this.stringToColor(u.name || u.phone), // 简单颜色生成
        }));
        this.currentUser = null
        this.searchForm.keyword = ''
      } catch (error) {
        this.$message.error('加载用户列表失败');
      }
    },
    async getdata() {
      const systeminfo = await this.$api.common.getsysdata()
      this.config = systeminfo.data
      const systemOilPrices = {}
      if (this.config.oil92 > 0) {
        systemOilPrices['92'] = this.config.oil92

      }
      if (this.config.oil95 > 0) {
        systemOilPrices['95'] = this.config.oil95
      }
      if (this.config.oil98 > 0) {
        systemOilPrices['98'] = this.config.oil98
      }
      if (this.config.oil0 > 0) {
        systemOilPrices['0'] = this.config.oil0
      }

      const chargesysret = await this.$api.bgyx.orpost(`bgyx/bgyx/getdata/chargesys`, {})

      this.chargesys = chargesysret.data




      this.systemOilPrices = systemOilPrices
      try {
        const ret = await this.$api.bgyx.alluser();
        this.userList = (ret.data || []).map(u => ({
          ...u,
          color: this.stringToColor(u.name || u.phone), // 简单颜色生成
        }));
      } catch (error) {
        this.$message.error('加载用户列表失败');
      }
    },
    // 输入匹配: 返回建议列表 (兼容el-autocomplete)
    querySearch(queryString, cb) {
      if (!queryString) {
        cb([]);
        return;
      }
      const results = this.userList.filter(user => {
        const kw = queryString.toLowerCase();
        return (user.phone && user.phone.toLowerCase().includes(kw)) ||
          (user.carnum && user.carnum.toLowerCase().includes(kw)) ||
          (user.name && user.name.toLowerCase().includes(kw));
      }).slice(0, 8); // 限制最多8条
      cb(results);
    },
    // 选择建议项
    handleSelectSuggestion(item) {
      this.currentUser = item;
      this.searchForm.keyword = item.phone; // 展示手机号
      this.searchResult = 'found';
      // 不自动弹窗，让用户点击右侧按钮，更符合直觉
    },
    // 点击搜索按钮时，如果当前有匹配用户但没有选择，尝试精确匹配第一个
    searchUser() {
      if (!this.searchForm.keyword) {
        this.$message.warning('请输入搜索关键词');
        return;
      }
      // 如果已有currentUser且关键词匹配其手机，直接打开弹窗？
      if (this.currentUser && this.currentUser.phone === this.searchForm.keyword) {
        this.showUserDialog = true;
        return;
      }
      this.searchResult = 'searching';
      // 调用后端精确搜索
      this.$api.bgyx.searchuser(this.searchForm.keyword).then(ret => {
        if (ret.success && ret.data) {
          const found = { ...ret.data, color: this.stringToColor(ret.data.name || ret.data.phone) };
          this.currentUser = found;
          this.searchResult = 'found';
          this.showUserDialog = true;
          // 更新列表
          this.getdata();
        } else {
          this.currentUser = null;
          this.searchResult = 'not found';
        }
      }).catch(() => {
        this.$message.error('搜索失败');
        this.searchResult = '';
      });
    },
    // 计算升数
    calculateLiter() {
      if (this.refuelForm.amount && this.currentOilPrice) {
        this.refuelForm.liter = (this.refuelForm.amount / this.currentOilPrice).toFixed(2);
        this.calculateActualAmount();
      }
    },
    calculateAmount() {
      if (this.refuelForm.liter && this.currentOilPrice) {
        this.refuelForm.amount = (this.refuelForm.liter * this.currentOilPrice).toFixed(2);
        this.calculateActualAmount();
      }
    },
    calculateActualAmount() {
      if (this.refuelForm.amount && this.currentUser?.discount) {
        const discount = this.currentUser.discount / 100;
        this.refuelForm.actualAmount = (this.refuelForm.amount * discount).toFixed(2);
      } else {
        this.refuelForm.actualAmount = this.refuelForm.amount;
      }
    },
    calculateWithCoupon() {
      // 优惠券选择变化时重新计算
    },
    calculateFinalAmount() {
      if (!this.refuelForm.actualAmount) return 0;
      let finalAmount = parseFloat(this.refuelForm.actualAmount);
      if (this.refuelForm.selectedCoupon) {
        finalAmount = Math.max(0, finalAmount - (this.refuelForm.selectedCoupon.money / 100));
      }
      return finalAmount.toFixed(2);
    },
    async loadAvailableCoupons() {
      if (!this.currentUser || !this.currentUser.id) return;
      
      this.couponLoading = true;
      try {
        const ret = await this.$api.bgyx.orpost(`bgyx/bgyx/coupon/mylist`, {
          uid: this.currentUser.id
        });
        
        if (ret.success) {
          const currentTime = Date.now();
          this.availableCoupons = (ret.data.coupons || []).filter(coupon => 
            coupon.status === '待使用' && 
            coupon.endtime > currentTime
          );
        }
      } catch (error) {
        console.error('加载优惠券失败:', error);
      } finally {
        this.couponLoading = false;
      }
    },
    async handleOpenRefuelDialog() {
      this.activeTab = 'refuel';
      // 加载用户可用的优惠券
      await this.loadAvailableCoupons();
      // 重置优惠券选择
      this.refuelForm.selectedCoupon = null;
      // 打开弹窗
      this.showUserDialog = true;
    },
    // 充值
    handleRecharge() {
      if (!this.rechargeForm.amount) {
        this.$message.warning('请输入充值金额');
        return;
      }
      
      if (this.confirmToggle) {
        this.confirmType = 'recharge';
        this.showConfirmDialog = true;
      } else {
        this.doRecharge();
      }
    },
    
    async doRecharge() {
      this.rechargeLoading = true;
      const pdata = {
        uid: this.currentUser.id,
        uname: this.currentUser.name,
        carnum: this.currentUser.carnum,
        phone: this.currentUser.phone,
        money: this.rechargeForm.amount,
        givem: this.rechargeForm.chargem ? this.rechargeForm.chargem : 0,
        note: this.rechargeForm.remark
      }

      console.log(pdata)

      const ret = await this.$api.bgyx.orpost(`bgyx/bgyx/rechare/money`, pdata)

      if (ret.success) {
        this.$message.success('充值成功');
        this.rechargeForm.amount = '';
        this.rechargeForm.remark = '';
        this.rechargeLoading = false;
        this.showUserDialog = false;
        this.showConfirmDialog = false;
        this.currentUser.money = this.currentUser.money + pdata.money * 100 + (pdata.givem ? pdata.givem * 100 : 0)
      } else {
        this.$message.error(ret.message);
        this.rechargeLoading = false;
        this.showConfirmDialog = false;
      }

      this.reloaduser()

    },
    // 加油
    handleRefuel() {
      if (!this.refuelForm.amount || parseFloat(this.refuelForm.amount) <= 0) {
        this.$message.warning('请输入有效的加油金额');
        return;
      }

      // 计算最终金额（考虑优惠券）
      const finalAmount = parseFloat(this.calculateFinalAmount());
      
      // 只有选择余额支付时才检查余额
      if (this.refuelForm.paymentMethod === 'balance' && finalAmount * 100 > this.currentUser.money) {
        this.$message.error('余额不足，请先充值[' + (finalAmount * 100 - this.currentUser.money) / 100 + '元]');
        return
      }
      
      // 检查优惠券使用条件
      if (this.refuelForm.selectedCoupon) {
        const minMoney = this.refuelForm.selectedCoupon.minmoney / 100;
        if (parseFloat(this.refuelForm.amount) < minMoney) {
          this.$message.error(`优惠券使用条件：满${minMoney}元可用`);
          return;
        }
      }
      
      if (this.confirmToggle) {
        this.confirmType = 'refuel';
        this.showConfirmDialog = true;
      } else {
        this.doRefuel();
      }
    },
    
    async doRefuel() {
      // 计算最终金额（考虑优惠券）
      const finalAmount = parseFloat(this.calculateFinalAmount());
      
      const pdata = {
        refuel: this.refuelForm,
        user: this.currentUser,
        ot: this.useUserPrice,
        p: this.systemOilPrices[this.refuelForm.oilType],
        paymentMethod: this.refuelForm.paymentMethod,
        coupon: this.refuelForm.selectedCoupon,
        finalAmount: finalAmount
      }
      console.log(pdata)

      this.refuelLoading = true;

      const ret = await this.$api.bgyx.orpost(`bgyx/bgyx/buy/oil`, pdata)

      if (ret.success) {
        this.$message.success('加油成功');
        if (this.currentUser && this.refuelForm.paymentMethod === 'balance') {
          this.currentUser.money = this.currentUser.money - finalAmount * 100
        }
        this.refuelForm = { amount: '', liter: '', oilType: '92', actualAmount: '', paymentMethod: 'balance', selectedCoupon: null };
        this.refuelLoading = false;
        this.showUserDialog = false;
        this.showConfirmDialog = false;

        this.reloaduser()
      }else{
        this.$message.error(ret.message);
        this.refuelLoading = false;
        this.showConfirmDialog = false;
      }

    },
    // 确认操作
    confirmOperation() {
      if (this.confirmType === 'recharge') {
        this.doRecharge();
      } else if (this.confirmType === 'refuel') {
        this.doRefuel();
      }
    },
    // 简单的字符串转颜色
    stringToColor(str) {
      if (!str) return '#409EFF';
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = hash % 360;
      return `hsl(${hue}, 70%, 60%)`;
    },
  },
};
</script>

<style scoped>
.quick-operation-container {
  padding: 24px;
  background-color: #f0f2f5;
  /* 整体柔和灰底，突出卡片白 */
  min-height: 100vh;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
}

/* 页头 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background: white;
  padding: 16px 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.header-left .el-breadcrumb {
  font-size: 14px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.confirm-toggle-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.toggle-hint-header {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.header-right .el-button {
  border-radius: 20px;
}

/* 主卡片 */
.main-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
}

.main-card:hover {
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
}

.search-wrapper {
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
}

.search-input :deep(.el-input__wrapper) {
  border-radius: 24px;
  padding-left: 20px;
  background-color: #f9fafc;
  box-shadow: 0 0 0 1px #e4e7ed inset;
  transition: 0.2s;
}

.search-input :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #c0c4cc inset;
}

.search-input :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 2px #409EFF inset;
}

.search-btn {
  border-radius: 24px;
  padding: 12px 28px;
  margin-right: -2px;
  font-weight: 500;
  letter-spacing: 1px;
}

/* 自定义下拉建议样式 */
:deep(.user-suggestion-popper) {
  border-radius: 12px;
  margin-top: 8px !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
}

.suggestion-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 13px;
}

.suggestion-name {
  font-weight: 600;
  color: #303133;
}

.suggestion-phone {
  color: #606266;
}

.suggestion-carnum {
  color: #409eff;
  background: #ecf5ff;
  padding: 2px 8px;
  border-radius: 12px;
}

/* 结果提示 */
.result-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 40px;
  background-color: white;
  margin: 16px 0 8px;
  font-size: 14px;
  border: 1px solid;
}

.result-message.error {
  color: #f56c6c;
  border-color: #fbc4c4;
  background-color: #fef0f0;
}

.result-message.info {
  color: #909399;
  border-color: #d9ecff;
  background-color: #ecf5ff;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 用户迷你卡片 */
.user-mini-card {
  display: flex;
  align-items: center;
  background: linear-gradient(145deg, #ffffff, #f8fafc);
  border-radius: 20px;
  padding: 20px 24px;
  margin-top: 16px;
  border: 1px solid #eef2f6;
  gap: 24px;
}

.user-mini-avatar {
  flex-shrink: 0;
}

.user-mini-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-mini-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.user-mini-row .label {
  font-size: 12px;
  color: #7f8c9a;
  font-weight: 400;
}

.user-mini-row .value {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  margin-right: 16px;
}

.user-mini-row.highlight .value.balance {
  color: #409EFF;
  font-weight: 600;
}

.user-mini-actions {
  display: flex;
  gap: 12px;
}

.user-mini-actions .el-button {
  border-radius: 30px;
  padding: 10px 20px;
  font-weight: 500;
}

.no-user-placeholder {
  text-align: center;
  padding: 40px 0;
  color: #a6b1c1;
  font-size: 14px;
  background: #fafbfd;
  border-radius: 24px;
}

.no-user-placeholder i {
  font-size: 48px;
  margin-bottom: 12px;
  color: #c0ccda;
}

/* 弹窗美化 */
.operation-dialog {
  border-radius: 20px;
}

.operation-dialog .el-dialog__header {
  border-bottom: 1px solid #f0f0f0;
  padding: 20px 24px;
}

.operation-dialog .el-dialog__title {
  font-weight: 600;
  color: #1e293b;
}

.dialog-user-context {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin: -10px 0 20px;
}

.dialog-tabs .el-tabs__header {
  margin-bottom: 24px;
}

.dialog-tabs .el-tabs__nav {
  border-radius: 30px;
  background: #f1f5f9;
  padding: 4px;
}

.dialog-tabs .el-tabs__item {
  border-radius: 30px;
  border: none !important;
  height: 40px;
  line-height: 40px;
  font-weight: 500;
  color: #64748b;
}

.dialog-tabs .el-tabs__item.is-active {
  background: white;
  color: #409eff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.operation-form .el-form-item {
  margin-bottom: 24px;
}

.operation-form .el-form-item__label {
  font-weight: 500;
  color: #334155;
  padding-bottom: 4px;
}

.form-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
}

.form-row .flex-item {
  flex: 1;
}

.form-row .switch-item {
  flex: 0 0 auto;
}

.price-preview {
  background: #f8fafc;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  font-size: 15px;
  color: #1e293b;
  margin-top: 8px;
}

.price-preview strong {
  font-size: 18px;
}

.actual-amount {
  color: #409eff;
}

.final-amount {
  color: #67c23a;
  font-size: 20px;
}

.dialog-footer-actions {
  text-align: center;
  margin-top: 32px;
}

.dialog-footer-actions .el-button {
  min-width: 180px;
  border-radius: 40px;
  height: 48px;
  font-size: 16px;
  font-weight: 500;
  box-shadow: 0 8px 18px rgba(64, 158, 255, 0.2);
}



/* 确认弹窗样式 */
.confirm-dialog-content {
  padding: 20px 0;
}

.confirm-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.confirm-item:last-child {
  border-bottom: none;
}

.confirm-label {
  font-size: 14px;
  color: #64748b;
}

.confirm-value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}

.confirm-value.amount {
  color: #409eff;
  font-size: 16px;
  font-weight: 600;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}
</style>